import { Controller } from "@hotwired/stimulus"

/**
 * Optional Headings Controller
 * Manages dynamic add/remove of optional heading/content pairs
 */
export default class extends Controller {
  static targets = ["container", "template"]

  // Map numeric indices to word-based field names for database columns
  static indexMap = {
    1: 'one',
    2: 'two',
    3: 'three',
    4: 'four'
  }

  connect() {
    console.log('[optional-headings] Controller connected')
    console.log('[optional-headings] Initial rows:', this.containerTarget.children.length)

    // If no rows exist, add one by default
    if (this.containerTarget.children.length === 0) {
      this.addRow()
    }

    // Update button states on initial load
    this.updateRemoveButtons()
    this.updateAddButton()
  }

  /**
   * Add a new optional heading/content row
   */
  addRow(event) {
    if (event) event.preventDefault()

    // Maximum 4 optional headings (database limitation)
    if (this.containerTarget.children.length >= 4) {
      console.log('[optional-headings] Maximum of 4 headings reached')
      alert('Maximum of 4 optional headings allowed')
      return
    }

    // Determine the next available index (1-4)
    const currentCount = this.containerTarget.children.length
    const numericIndex = currentCount + 1

    if (numericIndex > 4) {
      console.log('[optional-headings] Cannot add more than 4 headings')
      return
    }

    // Get word-based index (one, two, three, four)
    const wordIndex = this.constructor.indexMap[numericIndex]

    const template = this.templateTarget.content.cloneNode(true)
    const row = template.querySelector('[data-row]')

    // Replace INDEX placeholder with word-based index
    row.innerHTML = row.innerHTML.replace(/INDEX/g, wordIndex)

    // Add to container
    this.containerTarget.appendChild(row)

    console.log(`[optional-headings] Row added with index '${wordIndex}', total: ${this.containerTarget.children.length}`)

    // Focus on the heading input of the new row
    const newHeadingInput = row.querySelector('.optional-heading-input')
    if (newHeadingInput) {
      newHeadingInput.focus()
    }

    this.updateRemoveButtons()
    this.updateAddButton()
  }

  /**
   * Remove an optional heading/content row
   */
  removeRow(event) {
    event.preventDefault()

    const row = event.currentTarget.closest('[data-row]')
    if (!row) return

    // Always keep at least one row
    if (this.containerTarget.children.length <= 1) {
      // Clear the inputs instead of removing
      const inputs = row.querySelectorAll('input, textarea')
      inputs.forEach(input => input.value = '')
      console.log('[optional-headings] Cleared last row instead of removing')
      return
    }

    row.remove()
    console.log('[optional-headings] Row removed, total:', this.containerTarget.children.length)

    this.updateRemoveButtons()
    this.updateAddButton()
  }

  /**
   * Update remove button states (disable if only one row)
   */
  updateRemoveButtons() {
    const rows = this.containerTarget.children
    const removeButtons = this.containerTarget.querySelectorAll('[data-action*="removeRow"]')

    if (rows.length <= 1) {
      removeButtons.forEach(btn => {
        btn.disabled = true
        btn.classList.add('opacity-50')
        btn.title = 'Cannot remove the last row'
      })
    } else {
      removeButtons.forEach(btn => {
        btn.disabled = false
        btn.classList.remove('opacity-50')
        btn.title = 'Remove this optional heading'
      })
    }
  }

  /**
   * Update add button state (disable if 4 rows reached)
   */
  updateAddButton() {
    const addButton = document.querySelector('[data-action*="optional-headings#addRow"]')
    if (!addButton) return

    const rowCount = this.containerTarget.children.length

    if (rowCount >= 4) {
      addButton.disabled = true
      addButton.classList.add('opacity-50')
      addButton.title = 'Maximum of 4 optional headings allowed'
    } else {
      addButton.disabled = false
      addButton.classList.remove('opacity-50')
      addButton.title = 'Add a new optional heading'
    }
  }

  /**
   * Get current number of rows
   * @returns {number}
   */
  get rowCount() {
    return this.containerTarget.children.length
  }
}
